import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { METHODS, IMAGES } from "@/data/site";

export function MethodsList() {
  return (
    <div data-testid="page-methods" className="page-enter pt-32">
      <SEO
        title="Méthodes thérapeutiques — Psychologue Paris 16e"
        description="EMDR, hypnose, EFT, PNL, mindfulness, cohérence cardiaque, Strannik Psy, luminothérapie, holopsonie : approche intégrative."
      />
      <section className="container-fxv py-16">
        <Reveal>
          <p className="overline gold-line">Méthodes</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 max-w-3xl leading-[1.05]">
            Dix approches thérapeutiques au service d'une seule personne : vous
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#4A5568] mt-6 max-w-2xl text-lg leading-relaxed">
            La pratique intégrative repose sur l'idée qu'aucune méthode unique
            ne saurait répondre à toutes les situations. Découvrez les
            approches que je mobilise, leurs objectifs et leurs indications.
          </p>
        </Reveal>
      </section>

      <section className="container-fxv pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {METHODS.map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.04}>
              <Link
                to={`/methodes/${m.slug}`}
                data-testid={`method-list-${m.slug}`}
                className="group bg-white p-10 h-full block hover:bg-[#FAFAFA] transition-colors"
              >
                <p className="overline">
                  {String(i + 1).padStart(2, "0")} · Méthode
                </p>
                <h2 className="font-serif text-2xl text-[#0C2340] mt-4">
                  {m.name}
                </h2>
                <p className="text-[#4A5568] mt-4 leading-relaxed text-sm line-clamp-4">
                  {m.objectif}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#0C2340] group-hover:text-[#C5A059]">
                  Découvrir
                  <ArrowRight size={16} strokeWidth={1.5} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

export function MethodDetail() {
  const { slug } = useParams();
  const method = METHODS.find((m) => m.slug === slug);

  if (!method) {
    return (
      <div className="pt-40 container-fxv text-center" data-testid="method-not-found">
        <p className="font-serif text-3xl text-[#0C2340]">Page introuvable</p>
        <Link to="/methodes" className="text-[#C5A059] mt-4 inline-block">
          Retour aux méthodes
        </Link>
      </div>
    );
  }

  const others = METHODS.filter((m) => m.slug !== slug).slice(0, 4);

  return (
    <div data-testid={`page-method-${method.slug}`} className="page-enter pt-32">
      <SEO title={method.metaTitle} description={method.metaDescription} />

      <section className="container-fxv py-12">
        <Link
          to="/methodes"
          data-testid="method-back-link"
          className="text-sm text-[#4A5568] hover:text-[#0C2340] inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Toutes les méthodes
        </Link>
      </section>

      <section className="container-fxv pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="overline gold-line">Méthode thérapeutique</p>
          <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 leading-[1.05]">
            {method.name}
          </h1>
          <p className="text-[#4A5568] mt-8 text-lg leading-relaxed max-w-2xl">
            {method.objectif}
          </p>
        </div>
        <div className="lg:col-span-5">
          <img
            src={IMAGES.calmWater}
            alt=""
            className="w-full aspect-[5/4] object-cover"
          />
        </div>
      </section>

      <section className="bg-[#F0F4F8] section-fxv">
        <div className="container-fxv grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline gold-line">Fonctionnement</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0C2340] mt-6">
              Comment cette méthode opère-t-elle ?
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-[#4A5568] leading-relaxed">
              {method.fonctionnement}
            </p>
          </div>
        </div>
      </section>

      <section className="section-fxv bg-white">
        <div className="container-fxv grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline gold-line">Indications principales</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0C2340] mt-6">
              Pour qui, pour quoi ?
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
              {method.indications.map((i) => (
                <li
                  key={i}
                  className="py-5 text-[#0C2340] flex items-start gap-4"
                >
                  <span className="text-[#C5A059] font-serif text-2xl leading-none">·</span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid={`method-cta-${method.slug}`}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#0C2340] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#163861] transition-colors"
            >
              Prendre rendez-vous
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#0C2340] text-white section-fxv">
        <div className="container-fxv">
          <p className="overline">
            <span className="gold-line">Autres méthodes</span>
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((m) => (
              <Link
                key={m.slug}
                to={`/methodes/${m.slug}`}
                className="border border-[#1A3454] p-6 hover:border-[#C5A059] transition-colors"
              >
                <p className="font-serif text-xl text-white">{m.name}</p>
                <p className="text-xs text-[#C9D4E3] mt-3 line-clamp-3 leading-relaxed">
                  {m.objectif}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
